import { useCallback, useEffect, useRef, useState } from 'react';
import { testRunChat } from '../services/api';

/**
 * A governed conversation with one agent, and what the platform made of it.
 *
 * <h3>Why this is shared</h3>
 *
 * Two surfaces exercise an agent with policy live: the platform's Test Run
 * panel and Protect's Run Center. What a reply means — allowed, enforced,
 * blocked — is derived from the response, and a second copy of that derivation
 * is how two screens come to disagree about whether a call was blocked. That is
 * the one disagreement this product cannot afford.
 *
 * The rendering is deliberately not shared: the panel is drawn in v2 tokens and
 * Protect in p7.
 *
 * <h3>What the verdict is read from</h3>
 *
 * {@code stop_reason === 'policy_blocked'} or {@code _policyResult.blocked} is
 * BLOCKED; {@code _policyResult.llmEnforced} is ENFORCED; the presence of a
 * policy result at all is ALLOWED; and no policy result means the call was not
 * governed, which is reported as nothing rather than as a pass.
 */
export function useTestRun(agent, { onFirstPass } = {}) {
  const [messages, setMessages] = useState([]);
  const [sending, setSending] = useState(false);
  const [passed, setPassed] = useState(false);
  const passedRef = useRef(false);

  const reset = useCallback(() => {
    setMessages([]); setSending(false); setPassed(false); passedRef.current = false;
  }, []);

  useEffect(() => { reset(); }, [agent?.id, reset]);

  const send = useCallback(async (text) => {
    const trimmed = String(text || '').trim();
    if (!trimmed || sending || !agent?.id) return false;

    const userMsg = { role: 'user', content: trimmed, timestamp: new Date().toISOString() };
    const next = [...messages, userMsg];
    setMessages(next);
    setSending(true);

    /* Only user and assistant turns, and not the message just added — the API
       takes that one as its own field, so sending it twice would have the agent
       answer a question it had already been asked. */
    const history = next
      .filter((m) => m.role === 'user' || m.role === 'assistant')
      .map((m) => ({ role: m.role, content: m.content }))
      .slice(0, -1);

    try {
      /* The client's patience is sized from THIS agent's brief rather than a
         global constant: a cognitive agent's worst case is its iteration cap
         times the platform's per-call budget. */
      const raw = await testRunChat(agent.id, trimmed, history, {
        maxIterations: agent?.cognitiveBrief?.maxIterations ?? null,
      });
      const res = raw?.data || raw || {};

      if (res.ok === false || res.error) {
        setMessages((prev) => [...prev, {
          role: 'system', isError: true,
          content: res.error || 'The agent returned an error.',
          timestamp: new Date().toISOString(),
        }]);
        return false;
      }

      const content = Array.isArray(res.content)
        ? res.content.filter((c) => c.type === 'text').map((c) => c.text).join('\n')
        : (res.response || res.text || (typeof res === 'string' ? res : JSON.stringify(res)));

      let policyAction = null;
      if (res.stop_reason === 'policy_blocked') policyAction = 'BLOCKED';
      else if (res._policyResult?.blocked) policyAction = 'BLOCKED';
      else if (res._policyResult?.llmEnforced) policyAction = 'ENFORCED';
      else if (res._policyResult) policyAction = 'ALLOWED';

      setMessages((prev) => [...prev, {
        role: 'assistant',
        content: content || '(empty response)',
        toolResults: res._toolResults || null,
        policyAction,
        stopReason: res.stop_reason || null,
        timestamp: new Date().toISOString(),
      }]);

      if (!passedRef.current) {
        passedRef.current = true;
        setPassed(true);
        if (onFirstPass) onFirstPass();
      }
      return true;
    } catch (err) {
      /* An AbortError surfaces as "signal is aborted without reason", which
         reads as though the AGENT failed. It did not: the client stopped
         waiting while the run was still going. Say which, and what to do. */
      const aborted = err?.name === 'AbortError'
        || /aborted without reason|The user aborted a request/i.test(err?.message || '');
      setMessages((prev) => [...prev, {
        role: 'system', isError: true,
        content: aborted
          ? 'The client stopped waiting before the agent finished. The run is still executing '
            + 'server-side — check the audit trail for what it did. A cognitive agent reasons in '
            + 'a loop and can take minutes when it calls several tools.'
          : (err?.message || 'The run failed. Check that platform inference is configured.'),
        timestamp: new Date().toISOString(),
      }]);
      return false;
    } finally {
      setSending(false);
    }
  }, [agent, messages, sending, onFirstPass]);

  return { messages, sending, passed, send, reset };
}

export default useTestRun;
